import { createStore, type StoreApi } from "zustand/vanilla"

import type { Storage } from "../storage/storage"

export type ViewStateValue = null | boolean | number | string | ViewStateValue[] | { [key: string]: ViewStateValue }

export type ViewStateUpdater<T extends ViewStateValue> = T | ((current: T) => T)

interface ViewStateStoreState {
  values: Record<string, ViewStateValue>
  isRestored: boolean
}

interface PersistedViewState {
  version: number
  values: Record<string, ViewStateValue>
}

type ViewStateStore = StoreApi<ViewStateStoreState>

type PendingMutation = { type: "remove" } | { type: "set"; value: ViewStateValue }

type PendingStorageWrite = { type: "remove" } | { type: "set"; value: string }

const persistenceVersion = 1
const defaultStorageKey = "view-state"

export class ViewState {
  private readonly store: ViewStateStore
  private readonly restoredPromise: Promise<void>
  private readonly pendingMutations = new Map<string, PendingMutation>()
  private pendingStorageWrite: PendingStorageWrite | null = null
  private storageWriteInFlight = false
  private resolveRestored!: () => void

  constructor(
    private readonly storage: Storage,
    private readonly storageKey = defaultStorageKey
  ) {
    this.store = createStore<ViewStateStoreState>(() => ({
      values: {},
      isRestored: false,
    }))
    this.restoredPromise = new Promise<void>((resolve) => {
      this.resolveRestored = resolve
    })

    this.restore()
  }

  get isRestored() {
    return this.store.getState().isRestored
  }

  whenRestored() {
    return this.restoredPromise
  }

  get<T extends ViewStateValue>(key: string, fallback: T): T {
    const values = this.store.getState().values
    return (Object.hasOwn(values, key) ? values[key] : fallback) as T
  }

  set<T extends ViewStateValue>(key: string, value: T) {
    assertViewStateValue(value)

    const state = this.store.getState()
    if (Object.hasOwn(state.values, key) && Object.is(state.values[key], value)) return

    const values = {
      ...state.values,
      [key]: value,
    }
    this.store.setState({ values })

    if (!state.isRestored) {
      this.pendingMutations.set(key, { type: "set", value })
      return
    }

    this.persist(values)
  }

  remove(key: string) {
    const state = this.store.getState()
    const hasValue = Object.hasOwn(state.values, key)

    if (!state.isRestored) {
      this.pendingMutations.set(key, { type: "remove" })
    } else if (!hasValue) {
      return
    }

    if (hasValue) {
      const values = { ...state.values }
      delete values[key]
      this.store.setState({ values })

      if (state.isRestored) this.persist(values)
    }
  }

  subscribe(key: string, listener: () => void) {
    let value = this.store.getState().values[key]
    let isRestored = this.store.getState().isRestored

    return this.store.subscribe((state) => {
      const nextValue = state.values[key]
      if (Object.is(value, nextValue) && isRestored === state.isRestored) return
      value = nextValue
      isRestored = state.isRestored
      listener()
    })
  }

  private restore() {
    try {
      const stored = this.storage.getItem(this.storageKey)
      if (isPromiseLike<string | null>(stored)) {
        void Promise.resolve(stored)
          .then((value) => this.finishRestore(value))
          .catch(() => this.finishRestore(null))
        return
      }

      this.finishRestore(stored)
    } catch {
      this.finishRestore(null)
    }
  }

  private finishRestore(serialized: string | null) {
    if (this.store.getState().isRestored) return

    const pendingMutations = new Map(this.pendingMutations)
    this.pendingMutations.clear()

    const values = readPersistedValues(serialized)
    for (const [key, mutation] of pendingMutations) {
      if (mutation.type === "remove") delete values[key]
      else values[key] = mutation.value
    }

    this.store.setState({ values, isRestored: true })
    this.resolveRestored()

    if (pendingMutations.size > 0) this.persist(values)
  }

  private persist(values: Record<string, ViewStateValue>) {
    if (Object.keys(values).length === 0) {
      this.enqueueStorageWrite({ type: "remove" })
      return
    }

    const persisted: PersistedViewState = {
      version: persistenceVersion,
      values,
    }
    this.enqueueStorageWrite({
      type: "set",
      value: JSON.stringify(persisted),
    })
  }

  private enqueueStorageWrite(write: PendingStorageWrite) {
    if (this.storageWriteInFlight) {
      this.pendingStorageWrite = write
      return
    }

    this.flushStorageWrite(write)
  }

  private flushStorageWrite(write: PendingStorageWrite) {
    try {
      const result =
        write.type === "remove"
          ? this.storage.removeItem(this.storageKey)
          : this.storage.setItem(this.storageKey, write.value)

      if (!isPromiseLike<void>(result)) return

      this.storageWriteInFlight = true
      void Promise.resolve(result)
        .catch(() => undefined)
        .then(() => {
          this.storageWriteInFlight = false
          const pending = this.pendingStorageWrite
          this.pendingStorageWrite = null
          if (pending != null) this.flushStorageWrite(pending)
        })
    } catch {
      // Persistence failures must not make presentation state unusable in-memory.
    }
  }
}

export function assertViewStateValue(value: unknown): asserts value is ViewStateValue {
  assertJsonValue(value, new WeakSet<object>(), "ViewState value")
}

function assertJsonValue(value: unknown, ancestors: WeakSet<object>, path: string) {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return
  }

  if (typeof value === "number") {
    if (Number.isFinite(value)) return
    throw new Error(`${path} must contain only finite numbers.`)
  }

  if (typeof value !== "object") {
    throw new Error(`${path} must be JSON-serializable.`)
  }

  if (ancestors.has(value)) {
    throw new Error(`${path} must not contain circular references.`)
  }
  ancestors.add(value)

  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      if (!Object.hasOwn(value, index)) {
        throw new Error(`${path} must not contain sparse arrays.`)
      }
      assertJsonValue(value[index], ancestors, `${path}[${index}]`)
    }
    ancestors.delete(value)
    return
  }

  const prototype = Object.getPrototypeOf(value)
  if (prototype !== Object.prototype && prototype !== null) {
    throw new Error(`${path} must contain only plain objects.`)
  }

  for (const [key, child] of Object.entries(value)) {
    assertJsonValue(child, ancestors, `${path}.${key}`)
  }
  ancestors.delete(value)
}

function readPersistedValues(serialized: string | null): Record<string, ViewStateValue> {
  if (serialized == null) return {}

  try {
    const parsed = JSON.parse(serialized) as unknown
    if (parsed == null || typeof parsed !== "object") return {}

    const persisted = parsed as Partial<PersistedViewState>
    if (persisted.version !== persistenceVersion) return {}
    if (persisted.values == null || typeof persisted.values !== "object" || Array.isArray(persisted.values)) return {}

    const values: Record<string, ViewStateValue> = {}
    for (const [key, value] of Object.entries(persisted.values)) {
      try {
        assertViewStateValue(value)
        values[key] = value
      } catch {
        // Ignore corrupt entries without discarding unrelated persisted state.
      }
    }
    return values
  } catch {
    return {}
  }
}

function isPromiseLike<T>(value: unknown): value is PromiseLike<T> {
  return (
    value != null &&
    (typeof value === "object" || typeof value === "function") &&
    typeof (value as PromiseLike<T>).then === "function"
  )
}
